import {
    Box,
    Button,
    Grid,
    Typography,
    useTheme,
    CircularProgress
} from "@mui/material";
import { UserLocationMap } from "../../components/UserLocationMap";
import { useLocationOverview } from "../../features/location/useLocation";
import { useIpStats } from "../../features/location/useIpLookup";
import ExportCsvButton from "../../components/ExportCsvButton";

const LocationView = () => {
    const theme = useTheme();
    const { data: dashboardData, isLoading: isLocationLoading } = useLocationOverview();
    const { data: ipStats, isLoading: isIpLoading } = useIpStats();

    const isLoading = isLocationLoading || isIpLoading;

    const cardData = [
        {
            label: "Total Locations (countries)",
            value: dashboardData?.totalLocations?.toLocaleString() || 0,
            icon: (
                <img
                    src="/Images/typcn_location (2).png"
                    alt="Total Locations"
                    style={{ width: 24, height: 24 }}
                />
            ),
            color: theme.palette.primary.main,
            bg: theme.palette.primary.light,
        },
        {
            label: "Active Users",
            value: dashboardData?.activeUsers?.toLocaleString() || 0,
            icon: (
                <img
                    src="/Images/mingcute_user-pin-fill (1).png"
                    alt="Active Users"
                    style={{ width: 24, height: 24 }}
                />
            ),
            color: theme.palette.success.main,
            // bg: theme.palette.success.light,
        },
        {
            label: "New Users (past 30 days)",
            value: dashboardData?.newUsers?.toLocaleString() || 0,
            icon: (
                <img
                    src="/Images/mdi_user-add.png"
                    alt="New Users"
                    style={{ width: 24, height: 24 }}
                />
            ),
            color: theme.palette.warning.main,
            // bg: theme.palette.warning.light,
        },
        {
            label: "Inactive Users",
            value: dashboardData?.inactiveUsers?.toLocaleString() || 0,
            icon: (
                <img
                    src="Images/mingcute_user-x-fill.png"
                    alt="Inactive Users"
                    style={{ width: 24, height: 24 }}
                />
            ),
            color: theme.palette.error.main,
            // bg: theme.palette.error.light,
        },
    ];

    // Prepare map data using IP dots if available, similar to IpOverview
    const mapData = (ipStats?.ipDots || []).map(dot => ({
        ...dot,
        name: dot.city || dot.country || 'Location',
        count: dot.count || 1
    }));

    // Fallback to country data if no IP dots
    const displayData = mapData.length > 0 ? mapData : (dashboardData?.countryData || []);

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                bgcolor: "background.default",
                color: "text.primary",
                borderRadius: 2,
                minHeight: "100vh",
            }}
        >
            {/* Stats Cards */}
            <Grid container spacing={3} mb={4}>
                {cardData.map((card, idx) => (
                    <Grid item xs={12} sm={6} md={3} key={idx}>
                        <Box
                            elevation={1}
                            sx={{
                                borderRadius: 2,
                                p: 2,
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                                border: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" mb={1}>
                                {card.label}
                            </Typography>
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Typography variant="h5" fontWeight="bold">
                                    {card.value}
                                </Typography>
                                <Box
                                    sx={{
                                        borderRadius: 2,
                                        backgroundColor: card.color,
                                        width: 36,
                                        height: 36,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    {card.icon}
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                ))}
            </Grid>

            {/* Map Section */}
            <Box
                elevation={1}
                sx={{ borderRadius: 2, p: 3, mb: 4, border: `1px solid ${theme.palette.divider}` }}
            >
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                >


                    <Typography variant="h6" fontWeight="medium">
                        Location Map
                    </Typography>
                    <ExportCsvButton
                        data={dashboardData?.countryData || []}
                        filename="location-overview.csv"
                        columns={[
                            { label: "Country", key: "name" },
                            { label: "User Count", key: "count" }
                        ]}
                    />
                </Box>
                <UserLocationMap data={displayData} />
            </Box>
        </Box>
    );
};

export default LocationView;
